import { useState } from "react";

import { ClientResponse } from "@/Common/Api/ClientResponse";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IAuthenticatorInfo } from "@/Common/Models/UI/IAuthenticatorInfo";
import { ButtonControl } from "../Buttons/ButtonControl";
import { ExpandableSection } from "../ExpandableSection";
import { Modal } from "../Modals/Modal";

import styles from "./MultifactorAuthenticationPopup.module.scss";

interface IMultifactorAuthenticationPopupProps {
    authenticatorInfo: IAuthenticatorInfo;
    authenticatorCode: string;
    lastVerifiedEmail: string | null;
    currentEmail: string | null;

    onAuthenticatorCodeChange: (code: string) => void;
    onVerify: () => Promise<ClientResponse>;
    onSave: () => void;
    onCancel: () => void;
}

function MultifactorAuthenticationPopup(props: IMultifactorAuthenticationPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const [isApplicationMultifactorSectionOpen, setIsApplicationMultifactorSectionOpen] = useState<boolean>(true);
    const [isEmailSectionOpen, setIsEmailSectionOpen] = useState<boolean>(false);
    const [verifyResult, setVerifyResult] = useState<{ isSuccess: boolean, errors: string[] }>({ isSuccess: false, errors: [] });

    const isCodeValid = props.authenticatorCode?.length >= 6;
    const shouldShowEmailSection = cacheStore.ClinicSettings.EnableMultifactorAuthenticationByEmail;

    async function verify(): Promise<void> {
        const result = await props.onVerify();

        if (result.isSuccess) {
            setVerifyResult({ isSuccess: true, errors: [] });
            return;
        }

        setVerifyResult({
            isSuccess: false,
            errors: sessionStore.formatErrorsForDisplay(result.errors)
        });
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footerContainer}>
                <ButtonControl
                    className="btn btn-default"
                    onClick={() => props.onCancel()}
                    label={getText("CancelLabel")}
                />
                <ButtonControl
                    variant="brand"
                    label={getText("SaveLabel")}
                    onClick={() => props.onSave()}
                    disabled={!verifyResult.isSuccess || props.authenticatorInfo.IsEnabled}
                />
            </div>
        );
    }

    return (
        <Modal
            width={700}
            centerContent
            fixedOverlayPosition
            showOverlay
            disableOverlayClose
            showX
            onClose={props.onCancel}
            title={getText("MultifactorAuthenticationPopup_Title")}
            footer={renderFooter()}
            cssClass={styles.multifactorAuthenticationPopup}
            dataCy="settings-multifactor-auhtentication-popup"
        >
            <p>{getText("MultifactorAuthenticationPopupLabel_Introduction")}</p>
            <ExpandableSection
                title={getText("MultifactorAuthenticationPopupLabel_AuthenticatorAppLabel")}
                isOpen={isApplicationMultifactorSectionOpen}
                showChevronIcon={shouldShowEmailSection}
                lockOpen={!shouldShowEmailSection}
                onChevronClick={() => setIsApplicationMultifactorSectionOpen(!isApplicationMultifactorSectionOpen)}
                chevronLabel={(verifyResult.isSuccess || props.authenticatorInfo.IsEnabled) &&
                    <strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>
                }
            >
                <div className="inline-selection">
                    <div className="flex-grow-1">
                        <p className="bold">{getText("MultifactorAuthenticationPopupLabel_Step1Label")}</p>
                        <p className="padding-sm-top">{getText("MultifactorAuthenticationPopupLabel_Step1")}</p>
                        <p className="padding-md-top">{getText("MultifactorAuthenticationPopupLabel_Step1Alternative")}</p>
                        <p>{props.authenticatorInfo.ManualCode.match(/.{1,8}/g).join(" ")}</p>
                        <p className="bold padding-md-top">{getText("MultifactorAuthenticationPopupLabel_Step2Label")}</p>
                        <p className="padding-sm-top">{getText("MultifactorAuthenticationPopupLabel_Step2")}</p>
                        <div className={styles.verificationCodeGrid}>
                            <div>{getText("MultifactorAuthenticationPopupLabel_VerificationCode")}</div>
                            <div>
                                <TextboxControl
                                    disabled={verifyResult.isSuccess || props.authenticatorInfo.IsEnabled}
                                    IsNumeric
                                    maxLength={6}
                                    value={props.authenticatorCode}
                                    autoComplete="off"
                                    valid={isCodeValid || verifyResult.isSuccess || props.authenticatorInfo.IsEnabled}
                                    onChange={props.onAuthenticatorCodeChange}
                                    onKeyDown={e => {
                                        if (e.key === "Enter" && isCodeValid)
                                            verify();
                                    }}
                                />
                            </div>
                            <ButtonControl
                                disabled={!isCodeValid || verifyResult.isSuccess || props.authenticatorInfo.IsEnabled}
                                className="btn btn-primary margin-sm-left"
                                label={getText("VerifyLabel")}
                                onClick={verify}
                            />
                            {verifyResult.errors?.length > 0 && (
                                <>
                                    <div />
                                    {verifyResult.errors.map((error, index) => (
                                        <div key={index + "," + error} className={styles.verificationCodeError}>
                                            {error}
                                        </div>
                                    ))}
                                    <div />
                                </>
                            )}
                        </div>
                    </div>
                    <img src={props.authenticatorInfo.CodeImageUrl} />
                </div>
            </ExpandableSection>
            {shouldShowEmailSection && (
                <ExpandableSection
                    title={getText("EmailLabel")}
                    isOpen={isEmailSectionOpen}
                    onChevronClick={() => setIsEmailSectionOpen(!isEmailSectionOpen)}
                    chevronLabel={props.lastVerifiedEmail &&
                        <strong className={styles.verifiedLabel}>{getText("VerifiedLabel")}</strong>
                    }
                >
                    {props.lastVerifiedEmail || props.currentEmail || ""}
                </ExpandableSection>
            )}
        </Modal>
    );
}

export { MultifactorAuthenticationPopup };
